import React from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CampoEditavel from "./CampoEditavel";
import palette from "../../common/styles/palette";
import { InterviewSkill } from "../../types/Interview";

interface Props {
  skill: InterviewSkill;
  busy: boolean;
  aberta: boolean;
  idConteudo: string;
  onAlternar: () => void;
  onEditar: (skill: InterviewSkill, campo: "name" | "points", valor: string) => void;
  rotuloStatus?: string;
  rotuloStatusReferencia?: string;
  revisar?: boolean;
  onAbrirStatus?: () => void;
  onAdicionarItem?: () => void;
  onRemover?: () => void;
}

/** As duas câmeras editam a mesma seção com a mesma apresentação e ações. */
export default function CabecalhoSecaoRegras({ skill, busy, aberta, idConteudo, onAlternar,
  onEditar, rotuloStatus, revisar = false, onAbrirStatus, onAdicionarItem, onRemover,
  rotuloStatusReferencia,
}: Props) {
  const { t } = useTranslation();
  const tagStatus = rotuloStatus && onAbrirStatus ? (
    <Chip size="small" label={rotuloStatus} color={revisar ? "warning" : "success"}
      variant="outlined" clickable aria-label={`${rotuloStatus}. ${t("interview_open_first_flagged")}`}
      onClick={onAbrirStatus} sx={{ fontWeight: 600 }} />
  ) : null;
  return (
    <Box sx={{ p: 1.5, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", bgcolor: palette.brandSoft + "26" }}>
      <IconButton size="small" aria-expanded={aberta} aria-controls={idConteudo}
        aria-label={`${t(aberta ? "interview_collapse_section" : "interview_expand_section")} ${skill.name}`}
        onClick={onAlternar} sx={{ color: palette.brandDark }}>
        {aberta ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
      </IconButton>
      <Box sx={{ flex: "1 1 320px", minWidth: 240 }}>
        <CampoEditavel valor={skill.name} label={t("interview_section_label") ?? undefined}
          fullWidth multiline minRows={1} maxRows={3} confirmarComEnter disabled={busy}
          onComitar={(valor) => onEditar(skill, "name", valor)} />
      </Box>
      <Box sx={{ width: 96 }}>
        <CampoEditavel valor={skill.points === null ? "" : String(skill.points)} label={t("interview_field_points") ?? undefined}
          numero fullWidth disabled={busy} onComitar={(valor) => onEditar(skill, "points", valor)} />
      </Box>
      <Chip size="small" label={`${skill.items.length} ${t("interview_items")}`} variant="outlined" />
      {rotuloStatusReferencia ? (
        <Box sx={{ display: "grid", alignItems: "center", "& > *": { gridArea: "1 / 1", justifySelf: "start" } }}>
          {/* Reserva a largura da tag da câmera geral sem atribuir detecção a um item sem regra focal. */}
          <Chip size="small" label={rotuloStatusReferencia} variant="outlined" aria-hidden="true"
            sx={{ visibility: "hidden", pointerEvents: "none", fontWeight: 600 }} />
          {tagStatus}
        </Box>
      ) : tagStatus}
      {onAdicionarItem && <Button size="small" disabled={busy} onClick={onAdicionarItem}>{t("interview_add_item")}</Button>}
      {onRemover && <Button size="small" color="error" disabled={busy} onClick={onRemover}
        sx={{ textTransform: "none" }}>{t("interview_remove")}</Button>}
    </Box>
  );
}
